import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { pridajVyber } from "@/lib/import/vyberZostav";
import type { DataResponse, Sekcia } from "@/api/schemas";

/** Jeden kandidát z cenníka pre daný názov z TopSolidu. */
export interface MapovanieNavrh {
  material_id: string;
  kod: string;
  nazov: string;
  skore: number;
  /** Prečo je tu — v dialógu sa to zobrazí, aby sa nehádalo. */
  dovod: string;
}

export type DruhMapovania = "material" | "hrana" | "kovanie";

export interface MapovaniePolozka {
  druh: DruhMapovania;
  token: string;
  kontext: string;
  popis: string;
  pocet_vyskytov: number;
  navrhy: MapovanieNavrh[];
  naucene_material_id: string | null;
  naucene_zmazat: boolean;
  /** Kód a meno zapamätanej položky — riadok ich ukáže bez kliknutia (KOLO 204). */
  naucene_kod: string;
  naucene_nazov: string;
  /** Pracovná doska — pýta sa z PD cenníka, nie spomedzi plošných dosiek. */
  je_pracovna_doska: boolean;
  /**
   * Sekcia cenníka, keď nie je daná druhom — `sklo` pre kovanie od
   * Sklenárstva Mier (sklo inak pickery skrývajú, KOLO 166).
   */
  sekcia?: Sekcia | null;
  /** Čo appka vybrala podľa dekoru priradenej dosky (len pri hranách). */
  auto_material_id: string | null;
  auto_kod: string;
  auto_nazov: string;
  /** `42 × 1` — reálne použitý rozmer, aby bolo vidieť náhradu. */
  auto_rozmer: string;
  /** Dekor žiadanú hrúbku nemá — použila sa najbližšia dostupná. */
  auto_nahradena: boolean;
  /** Odkaz na vybranú hranu u dodávateľa a jej kód (KOLO 135). */
  auto_eshop_odkaz?: string | null;
  auto_eshop_kod?: string;
  /** Materiál dielca, na ktorom hrana je (`K682`) — zoskupenie hrán. */
  material_popis: string;
  /** Hrúbky dielcov, na ktorých sa hrana vyskytla (Decimal ako string). */
  hrubky_dielca: string[];
  /** `22x05` → 22 a 0,5. Šírka je z kódu v exporte, nie dopočítaná. */
  sirka_hrany_mm: number | null;
  hrubka_hrany_mm: string | null;
  /**
   * Kde je položka v exporte (KOLO 216) — dielce v zostavách pri materiáli,
   * kusy po zostavách pri kovaní (`dielec` prázdny). Starší server ich
   * neposiela.
   */
  vyskyty?: VyskytPolozky[];
  /** Dĺžka hrany v exporte v mm (strany × kusy, bez nadmiery) — len hrany. */
  dlzka_mm?: string | number;
  /**
   * Hrúbka nad stropom z Nastavení (ohýbaný dielec bez Rozvinu, KOLO 216) —
   * doska sa priradiť nedá, import ju pustí len ako Nenahrávať.
   */
  nemozna_hrubka?: boolean;
  /**
   * Hrúbka materiálu, ktorý riadok má (voľba > zo zákazky > zapamätané),
   * a ktorého (KOLO 217) — dielce ju pri importe dostanú. Len materiály.
   */
  hrubka_materialu?: string | null;
  hrubka_material_id?: string | null;
  /** Materiál inej triedy hrúbky ako riadok (0,8 k 1 mm, nie 18,7 k 18). */
  ina_hrubka?: boolean;
  /**
   * Pri aktualizácii zákazky: materiál či hrana, ktorú majú dielce riadku
   * v zákazke teraz — predvyplní sa pred zapamätaným (KOLO 217).
   */
  zakazka_material_id?: string | null;
  zakazka_kod?: string;
  zakazka_nazov?: string;
}

export interface VyskytPolozky {
  zostava_kod: string;
  dielec: string;
  pocet_ks: number;
  x_mm?: string | null;
  y_mm?: string | null;
}

/** Priradenie dosiek pre server — `{"k682|18": "<uuid>"}`. */
export type Materialy = Record<string, string>;

/**
 * Ručné „je to pracovná / plošná doska" (KOLO 168) — `{"k209|38": false}`.
 *
 * Kľúč ako pri `Materialy`. Server podľa toho prepočíta
 * `je_pracovna_doska` aj návrhy — 38 mm nemusí byť pracovná doska
 * (škárovka 38) a pracovná doska nemusí mať 38 mm.
 */
export type PracovneDosky = Record<string, boolean>;

/**
 * Čo stroj s dielcom spraví — stĺpce ako v DAEX skripte.
 *
 * Vyplnené vždy, keď operácia nie je prázdna, teda **aj pri predreze**;
 * do KOLA 88 sa hlásilo len združenie a predrez nebolo vidieť vôbec.
 */
export interface PrirezNahlad {
  /** `rez` | `zdruz_rez` | `rot_rez` | `rot_zdruz_rez` */
  operacia: string;
  /** Používateľský názov z Nastavení (`nc_rez`…). */
  operacia_nazov: string;
  /** Označenie pre dielňu — `nc_zdruz_rez_2x818x36`. */
  oznacenie: string;
  /** Rozmer prírezu v osiach dielca (skript: NOVA X / NOVA Y). */
  nova_x_mm: string;
  nova_y_mm: string;
  kusov_v_prireze: number;
  /** Koľko prírezov z toho vyjde spolu (skript: KS PO). */
  prirezov: number;
}

/** Čo import s dielcom spraví, kým sa to nestalo. */
export interface DielecNahlad {
  zostava_kod: string;
  kod: string;
  nazov: string;
  pocet_ks: number;
  x_mm: string;
  y_mm: string;
  z_mm: string | null;
  operacia: string | null;
  /** Hrúbky vrstiev, keď sa lepí; null = zostáva vcelku. */
  delenie: string[] | null;
  /** Reálne sa otočí (nie len navrhuje to názov). */
  rotacia: boolean;
  /** Zmestí sa do rotačného priestoru stroja? Ak nie, prepínač je vypnutý. */
  rotacia_mozna: boolean;
  prirez: PrirezNahlad | null;
  /**
   * Rozmer, na ktorý sa dielec reálne odreže — **vyplnený vždy**, aj keď
   * stroj nič nerobí a `prirez` je preto null. Do KOLA 105 sa čítal
   * z `prirez`, takže tabuľka Delenie ukazovala pri `plat` 1602×502
   * rozmer dielca namiesto prírezu 1607×507.
   */
  prirez_x_mm: string;
  prirez_y_mm: string;
  /**
   * Prírez **pred zdvihom na minimum upnutia** — čistý rozmer plus
   * nadmiera. Podľa neho sa rozhoduje oranžová v stĺpci „Podľa modelu"
   * aj nález `nadmiera_nesedi` (KOLO 113): model rieši nadmiery,
   * predĺženia na stroj nie.
   */
  prirez_bez_upnutia_x_mm: string;
  prirez_bez_upnutia_y_mm: string;
  /**
   * Koľko mm ubrali z osi hrany — **kladné čísla** (KOLO 115). Nadmiera
   * je jedno znamienkové číslo (`základ − odpočet`), takže sa z nej
   * zložky späť nevytiahnu; odčítaním stĺpcov Čistý a Prírez by vznikla
   * druhá kópia pravidla. Preto to počíta backend.
   */
  odpocet_x_mm: string;
  odpocet_y_mm: string;
  /** `HRUBA_*` zo surového exportu — čo o príreze hovorí TopSolid. */
  hruba_x_mm: string | null;
  hruba_y_mm: string | null;
  /**
   * Sedí model s pravidlom? `null` = export `HRUBA_*` nemá.
   *
   * Podľa toho sa farbí stĺpec „TopSolid". **Nepočítať si to tu znova**
   * (KOLO 116): pri postupe s vlastným predfrezom sa od modelu vedome
   * líšime o hrúbku hrán, a to vie len backend. Keď si frontend
   * porovnanie opísal, po zmene pravidla v KOLE 115 ostal na starom —
   * nález zhasol, oranžová svietila ďalej na každom lepenom dielci.
   */
  sedi_s_modelom: boolean | null;
  /** Zaradenie prišlo z pamäte, nie z exportu. */
  naucene_zaradenie: boolean;
  /**
   * Neolepený dielec, ktorý TopSolid vypíše pod olepeným (KOLO 173).
   * `null` = export ho nemá, odpočet ide len z hrán na stranách.
   */
  neolepeny_x_mm?: string | null;
  neolepeny_y_mm?: string | null;
  /**
   * Hrany, ktoré export nepriradil žiadnej strane (zaoblený roh) — podľa
   * neolepeného dielca. V `odpocet_*` sú len pri postupe najprv na CNC.
   */
  hrany_navyse_x_mm?: string;
  hrany_navyse_y_mm?: string;
  /** Postup reže holú dosku (predfrez 0) — riadok tabuľky Odčítanie hrán. */
  najprv_cnc?: boolean;
}

/** Jeden nález do zoznamu „Na opravu v TopSolide". */
export interface Oprava {
  druh: string;
  zostava_kod: string;
  dielec: string;
  popis: string;
}

/**
 * Zaradenie položky — `{"uch_xh02_320": "kovanie"}`.
 *
 * Export rozhoduje podľa `ZPRACOVANI=hw` a rozmerov, takže úchytka
 * s rozmermi a bez `hw` skončí medzi dielcami. Kľúč je **názov**, nie
 * dvojica (zostava, kód) — tá istá úchytka býva v desiatich zostavách.
 */
export type Zaradenie = Record<string, "dielec" | "kovanie">;

export interface TopsolidAnalyza {
  zostav: number;
  dielcov: number;
  /** Koľko dielcov reálne vznikne — sendvič je rodič + N vrstiev. */
  dielcov_po_importe: number;
  kovani: number;
  /**
   * Číslo a názov zákazky z `OZNACENI_HLAVNI_SESTAVY` (KOLO 125).
   *
   * Číslo býva `null` — nesie ho len časť exportov (`AA6305_PMS_Molek`
   * áno, `Reviko_ADD` nie) a hádať ho z nečíselného prefixu by založilo
   * zákazku pod nezmyslom. Názov sa dá odvodiť skoro vždy.
   */
  zakazka_kod?: string | null;
  zakazka_nazov?: string;
  materialy: MapovaniePolozka[];
  hrany: MapovaniePolozka[];
  kovania: MapovaniePolozka[];
  dielce: DielecNahlad[];
  opravy: Oprava[];
}

/**
 * Voľba delenia per hrúbka — `{"36": [18, 18]}` alebo `{"36": null}`.
 *
 * Mení otázky na materiál, takže sa posiela na server a analýza sa
 * prepočíta. UI si nové otázky domyslieť nevie — návrhy priradenia
 * chodia z cenníka.
 */
export type Delenie = Record<string, number[] | null>;

/** Voľba rotácie pre jeden dielec z náhľadu. */
export interface RotaciaVolba {
  zostava_kod: string;
  kod: string;
  rotovat: boolean;
}

export interface Rozhodnutie {
  druh: DruhMapovania;
  token: string;
  kontext: string;
  material_id: string | null;
  zmazat: boolean;
  /** Uložiť do pamäte? False = platí len pre tento import. */
  zapamatat: boolean;
}

export interface SurovyImportVysledok {
  zakazka_id: string;
  varianta_id: string;
  zostav: number;
  dielcov: number;
  kovani: number;
  setov: number;
}

/**
 * Analýza surového TopSolid exportu — čo treba priradiť na cenník.
 *
 * Nič neukladá, je to otázka. Súbor sa posiela znova aj pri potvrdení:
 * držať rozpracovaný upload na serveri medzi krokmi by znamenalo session
 * a upratovanie sirôt, pričom medzi krokmi sa mení len mapovanie.
 */
export function useTopsolidAnalyza(): UseMutationResult<
  DataResponse<TopsolidAnalyza>,
  Error,
  {
    file: File;
    delenie?: Delenie;
    zaradenie?: Zaradenie;
    materialy?: Materialy;
    pracovneDosky?: PracovneDosky;
    /** Len vybrané zostavy z Tandem Zákazky (KOLO 175) */
    zostavyVybrane?: string[] | null;
    /** Aktualizovaná varianta — riadky dostanú materiál z nej (KOLO 217) */
    variantaId?: string | null;
  }
> {
  return useMutation({
    mutationFn: async ({
      file,
      delenie,
      zaradenie,
      materialy,
      pracovneDosky,
      zostavyVybrane,
      variantaId,
    }) => {
      const fd = new FormData();
      fd.append("file", file);
      if (delenie && Object.keys(delenie).length > 0) {
        fd.append("delenie_json", JSON.stringify(delenie));
      }
      if (zaradenie && Object.keys(zaradenie).length > 0) {
        fd.append("zaradenie_json", JSON.stringify(zaradenie));
      }
      if (materialy && Object.keys(materialy).length > 0) {
        fd.append("materialy_json", JSON.stringify(materialy));
      }
      if (pracovneDosky && Object.keys(pracovneDosky).length > 0) {
        fd.append("pracovne_dosky_json", JSON.stringify(pracovneDosky));
      }
      pridajVyber(fd, zostavyVybrane);
      if (variantaId) fd.append("varianta_id", variantaId);
      const { data } = await api.post<DataResponse<TopsolidAnalyza>>(
        "/import/topsolid/analyza",
        fd,
      );
      return data;
    },
  });
}

/** Spoločné polia potvrdenia exportu — nová zákazka aj nová časť (KOLO 203). */
interface PotvrdenieExportu {
  file: File;
  rozhodnutia: Rozhodnutie[];
  rotacie?: RotaciaVolba[];
  delenie?: Delenie;
  zaradenie?: Zaradenie;
  materialy?: Materialy;
  pracovneDosky?: PracovneDosky;
  /** Mená zostáv z Tandem Zostáv (KOLO 146) */
  zostavyMena?: Record<string, string> | null;
  /** Len vybrané zostavy z Tandem Zákazky (KOLO 175) */
  zostavyVybrane?: string[] | null;
}

function formularPotvrdenia(p: PotvrdenieExportu): FormData {
  const fd = new FormData();
  fd.append("file", p.file);
  if (p.delenie && Object.keys(p.delenie).length > 0) {
    fd.append("delenie_json", JSON.stringify(p.delenie));
  }
  if (p.zaradenie && Object.keys(p.zaradenie).length > 0) {
    fd.append("zaradenie_json", JSON.stringify(p.zaradenie));
  }
  if (p.materialy && Object.keys(p.materialy).length > 0) {
    fd.append("materialy_json", JSON.stringify(p.materialy));
  }
  if (p.pracovneDosky && Object.keys(p.pracovneDosky).length > 0) {
    fd.append("pracovne_dosky_json", JSON.stringify(p.pracovneDosky));
  }
  if (p.zostavyMena && Object.keys(p.zostavyMena).length > 0) {
    fd.append("zostavy_mena_json", JSON.stringify(p.zostavyMena));
  }
  pridajVyber(fd, p.zostavyVybrane);
  fd.append(
    "rozhodnutia_json",
    JSON.stringify({ rozhodnutia: p.rozhodnutia, rotacie: p.rotacie ?? [] }),
  );
  return fd;
}

export function useTopsolidPotvrdit(): UseMutationResult<
  DataResponse<SurovyImportVysledok>,
  Error,
  PotvrdenieExportu & {
    cislo_zakazky: string;
    nazov?: string;
    /** Bez čísla (KOLO 200): `neskor` = výpočet V, `z` = malá zákazka Z */
    bezCislaTyp?: "neskor" | "z" | null;
    /** Cesta výkresu z Tandem Zákazky — podľa nej sa nájde pri ďalšom odoslaní */
    vykresCesta?: string | null;
    /** Výroba patrí k výpočtu (KOLO 208) — nezaloží zákazku, pribudne doň. */
    kVypoctuId?: string | null;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cislo_zakazky,
      bezCislaTyp,
      vykresCesta,
      nazov,
      kVypoctuId,
      ...p
    }) => {
      const fd = formularPotvrdenia(p);
      fd.append("cislo_zakazky", cislo_zakazky);
      if (bezCislaTyp) fd.append("bez_cisla_typ", bezCislaTyp);
      if (vykresCesta) fd.append("vykres_cesta", vykresCesta);
      if (nazov) fd.append("nazov", nazov);
      if (kVypoctuId) fd.append("k_vypoctu_id", kVypoctuId);
      const { data } = await api.post<DataResponse<SurovyImportVysledok>>(
        "/import/topsolid/potvrdit",
        fd,
      );
      return data;
    },
    onSuccess: () => void qc.invalidateQueries(),
  });
}

/**
 * Export ďalšej časti zákazky → nová varianta-časť (KOLO 203). Doterajší
 * jediný výpočet zákazky bez častí sa stane prvou časťou s menom
 * `doterajsiaNazov`; jeho dielce sa nemenia.
 */
export function useTopsolidPridajCast(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<SurovyImportVysledok>,
  Error,
  PotvrdenieExportu & { castNazov: string; doterajsiaNazov?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ castNazov, doterajsiaNazov, ...p }) => {
      const fd = formularPotvrdenia(p);
      fd.append("cast_nazov", castNazov);
      if (doterajsiaNazov) fd.append("doterajsia_cast_nazov", doterajsiaNazov);
      const { data } = await api.post<DataResponse<SurovyImportVysledok>>(
        `/import/topsolid/cast/${zakazkaId}`,
        fd,
      );
      return data;
    },
    onSuccess: () => void qc.invalidateQueries(),
  });
}

/** Naučené priradenie — čo si appka pamätá z minulých importov. */
export interface NaucenePriradenie {
  id: string;
  druh: DruhMapovania;
  token: string;
  kontext: string;
  material_id: string | null;
  material_kod: string | null;
  material_nazov: string | null;
  zmazat: boolean;
  pocet_pouziti: number;
  naposledy_at: string | null;
}

/**
 * Zoznam naučených priradení.
 *
 * Pamäť, ktorá sa nedá prezrieť a opraviť, je pasca — raz zle priradený
 * materiál by sa ticho opakoval pri každom ďalšom importe.
 */
export function useNaucenePriradenia(
  filter: { druh?: string; q?: string } = {},
): UseQueryResult<DataResponse<NaucenePriradenie[]>> {
  return useQuery({
    queryKey: queryKeys.topsolidMapovanie.list(
      filter.druh ?? "",
      filter.q ?? "",
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter.druh) params.set("druh", filter.druh);
      if (filter.q) params.set("q", filter.q);
      const { data } = await api.get<DataResponse<NaucenePriradenie[]>>(
        `/topsolid-mapovanie?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useZmazatPriradenie(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/topsolid-mapovanie/${id}`);
    },
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: queryKeys.topsolidMapovanie.all,
      }),
  });
}

/**
 * Čo treba opraviť v TopSolide — nálezy z importu tejto zákazky.
 *
 * Zoznam vzniká pri importe. Bez neho by tá istá chyba prišla aj pri
 * ďalšom exporte a obchádzala by sa znova.
 */
export function useOpravyZakazky(
  zakazkaId: string | null,
): UseQueryResult<DataResponse<Oprava[]>> {
  return useQuery({
    queryKey: queryKeys.topsolidOpravy.zakazka(zakazkaId ?? ""),
    enabled: Boolean(zakazkaId),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Oprava[]>>(
        `/import/topsolid/opravy/${zakazkaId}`,
      );
      return data;
    },
  });
}
